import React, {
	useState,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { Button, Select, message, Modal } from 'antd';
import './PlanContent.css';
import { plansApi } from '../../api/plans';

const { Option } = Select;

const BLOCK_TYPES = [
	'worship',
	'sermon',
	'scripture',
	'prayer',
	'announcement',
	'other',
];

function PlanContent({
	plan,
	orgData,
	onPlanUpdate,
	isEditMode,
	onDeleteBlock,
}) {
	const [collapsedSections, setCollapsedSections] = useState(new Set());
	const [collapsedBlocks, setCollapsedBlocks] = useState(new Set());
	const [isDirty, setIsDirty] = useState(false);
	const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
	const [selectedBlockId, setSelectedBlockId] = useState(null);
	const [selectedSectionId, setSelectedSectionId] = useState(null);
	const [selectedMediaIds, setSelectedMediaIds] = useState(new Set());

	// Add this useEffect to initialize selected media when modal opens
	useEffect(() => {
		if (isMediaModalOpen && selectedBlockId) {
			// Find the current block's media
			const currentBlock = plan.sections
				.flatMap((section) => section.blocks)
				.find((block) => block.id === selectedBlockId);

			// Initialize with current media IDs
			if (currentBlock?.media) {
				setSelectedMediaIds(
					new Set(currentBlock.media.map((m) => m.id))
				);
			} else {
				setSelectedMediaIds(new Set());
			}
		}
	}, [isMediaModalOpen, selectedBlockId, plan.sections]);

	const debounce = (func, wait) => {
		let timeout;
		return (...args) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => func(...args), wait);
		};
	};

	// Helper function to convert hex to RGB
	const hexToRgb = (hex) => {
		// Remove the # if present
		hex = hex.replace('#', '');

		// Parse the hex values
		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);

		return `${r}, ${g}, ${b}`;
	};

	// Create CSS variables for organization colors
	const styleVars = {
		'--primary-color': orgData?.primaryColor || '#4b95a3',
		'--primary-color-light': `${orgData?.primaryColor}15` || '#4b95a315',
		'--secondary-color': orgData?.secondaryColor || '#3700b3',
		'--secondary-color-rgb': hexToRgb(orgData?.secondaryColor || '#3700b3'),
		'--secondary-color-light':
			`${orgData?.secondaryColor}10` || '#3700b310',
		'--primary-color-dark': `${orgData?.primaryColor}30` || '#4b95a330',
		'--secondary-color-dark': `${orgData?.secondaryColor}20` || '#3700b320',
	};

	const toggleSection = (sectionId) => {
		const newCollapsed = new Set(collapsedSections);
		if (newCollapsed.has(sectionId)) {
			newCollapsed.delete(sectionId);
		} else {
			newCollapsed.add(sectionId);
		}
		setCollapsedSections(newCollapsed);
	};

	const toggleBlock = (blockId) => {
		const newCollapsed = new Set(collapsedBlocks);
		if (newCollapsed.has(blockId)) {
			newCollapsed.delete(blockId);
		} else {
			newCollapsed.add(blockId);
		}
		setCollapsedBlocks(newCollapsed);
	};

	const handleSectionChange = (sectionId, field, value) => {
		const updatedSections = plan.sections.map((section) => {
			if (section.id === sectionId) {
				return { ...section, [field]: value };
			}
			return section;
		});
		const updatedPlan = { ...plan, sections: updatedSections };
		onPlanUpdate(updatedPlan);
		setIsDirty(true);
	};

	const handleRemoveSection = async (sectionId) => {
		try {
			// Update local state immediately
			const updatedSections = plan.sections.filter(
				(section) => section.id !== sectionId
			);
			onPlanUpdate({ ...plan, sections: updatedSections });

			// Call the API endpoint to delete the section
			await plansApi.deleteSection(sectionId);
		} catch (error) {
			console.error('Error removing section:', error);
			message.error('Failed to remove section');
		}
	};

	const handleBlockChange = (sectionId, blockId, field, value) => {
		const updatedSections = plan.sections.map((section) => {
			if (section.id === sectionId) {
				const updatedBlocks = section.blocks.map((block) => {
					if (block.id === blockId) {
						return { ...block, [field]: value };
					}
					return block;
				});
				return { ...section, blocks: updatedBlocks };
			}
			return section;
		});
		const updatedPlan = { ...plan, sections: updatedSections };
		onPlanUpdate(updatedPlan);
		setIsDirty(true);
	};

	const handleAddSection = () => {
		const newSection = {
			id: `temp_${Date.now()}`,
			title: 'New Section',
			description: '',
			estimatedDuration: 0,
			order: (plan.sections?.length || 0) + 1,
			blocks: [],
		};

		onPlanUpdate({
			...plan,
			sections: [...(plan.sections || []), newSection],
		});
	};

	const handleAddBlock = (sectionId) => {
		const newBlock = {
			id: `temp_${Date.now()}_${Math.random()
				.toString(36)
				.substring(2, 15)}`,
			type: 'other',
			title: 'New Block',
			content: '',
			notes: '',
			estimatedDuration: 0,
			order:
				(plan.sections.find((s) => s.id === sectionId)?.blocks
					?.length || 0) + 1,
			media: [],
		};

		const updatedSections = plan.sections.map((section) => {
			if (section.id === sectionId) {
				return {
					...section,
					blocks: [...(section.blocks || []), newBlock],
				};
			}
			return section;
		});
		onPlanUpdate({ ...plan, sections: updatedSections });
	};

	const handleRemoveBlock = async (sectionId, blockId) => {
		try {
			// Update local state immediately
			const updatedSections = plan.sections.map((section) => {
				if (section.id === sectionId) {
					return {
						...section,
						blocks: section.blocks.filter(
							(block) => block.id !== blockId
						),
					};
				}
				return section;
			});
			onPlanUpdate({ ...plan, sections: updatedSections });

			// Call the API endpoint to delete the block
			await plansApi.deleteBlock(blockId);
		} catch (error) {
			console.error('Error removing block:', error);
			message.error('Failed to remove block');
		}
	};

	const handleMoveSection = (sectionId, direction) => {
		const sectionIndex = plan.sections.findIndex((s) => s.id === sectionId);
		const currentSection = plan.sections[sectionIndex];

		// Calculate new order
		const newOrder =
			direction === 'up'
				? currentSection.order - 1
				: currentSection.order + 1;

		// Update orders locally
		const updatedSections = [...plan.sections];
		const swapIndex =
			direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;

		if (swapIndex >= 0 && swapIndex < updatedSections.length) {
			// Swap orders between sections
			updatedSections[sectionIndex] = {
				...currentSection,
				order: newOrder,
			};
			updatedSections[swapIndex] = {
				...updatedSections[swapIndex],
				order: currentSection.order,
			};

			// Sort sections by order
			updatedSections.sort((a, b) => a.order - b.order);

			// Update plan
			const updatedPlan = { ...plan, sections: updatedSections };
			onPlanUpdate(updatedPlan);
			setIsDirty(true);
		}
	};

	const handleMoveBlock = (blockId, currentSectionId, direction) => {
		const currentSectionIndex = plan.sections.findIndex(
			(s) => s.id === currentSectionId
		);
		const currentSection = plan.sections[currentSectionIndex];
		const blockIndex = currentSection.blocks.findIndex(
			(b) => b.id === blockId
		);
		const currentBlock = currentSection.blocks[blockIndex];

		// Create copy of sections to modify
		const updatedSections = [...plan.sections];

		// Moving up from first position in section
		if (
			direction === 'up' &&
			currentBlock.order === 1 &&
			currentSectionIndex > 0
		) {
			// Get previous section
			const prevSection = updatedSections[currentSectionIndex - 1];

			// Remove block from current section
			const updatedCurrentBlocks = currentSection.blocks.filter(
				(b) => b.id !== blockId
			);
			// Reorder remaining blocks in current section
			updatedCurrentBlocks.forEach((block, idx) => {
				block.order = idx + 1;
			});

			// Add block to end of previous section
			const newOrder = (prevSection.blocks?.length || 0) + 1;
			const movedBlock = { ...currentBlock, order: newOrder };
			const updatedPrevBlocks = [
				...(prevSection.blocks || []),
				movedBlock,
			];

			// Update both sections
			updatedSections[currentSectionIndex - 1] = {
				...prevSection,
				blocks: updatedPrevBlocks,
			};
			updatedSections[currentSectionIndex] = {
				...currentSection,
				blocks: updatedCurrentBlocks,
			};
		}
		// Moving down from last position in section
		else if (
			direction === 'down' &&
			currentBlock.order === currentSection.blocks.length &&
			currentSectionIndex < updatedSections.length - 1
		) {
			// Get next section
			const nextSection = updatedSections[currentSectionIndex + 1];

			// Remove block from current section
			const updatedCurrentBlocks = currentSection.blocks.filter(
				(b) => b.id !== blockId
			);
			// Reorder remaining blocks in current section
			updatedCurrentBlocks.forEach((block, idx) => {
				block.order = idx + 1;
			});

			// Add block to beginning of next section
			const movedBlock = { ...currentBlock, order: 1 };
			const updatedNextBlocks = [
				movedBlock,
				...(nextSection.blocks || []),
			];
			// Reorder blocks in next section
			updatedNextBlocks.forEach((block, idx) => {
				block.order = idx + 1;
			});

			// Update both sections
			updatedSections[currentSectionIndex] = {
				...currentSection,
				blocks: updatedCurrentBlocks,
			};
			updatedSections[currentSectionIndex + 1] = {
				...nextSection,
				blocks: updatedNextBlocks,
			};
		}
		// Moving within the same section
		else {
			const updatedBlocks = [...currentSection.blocks];
			const swapIndex =
				direction === 'up' ? blockIndex - 1 : blockIndex + 1;

			if (swapIndex >= 0 && swapIndex < updatedBlocks.length) {
				// Swap orders between blocks
				const newOrder =
					direction === 'up'
						? currentBlock.order - 1
						: currentBlock.order + 1;
				updatedBlocks[blockIndex] = {
					...currentBlock,
					order: newOrder,
				};
				updatedBlocks[swapIndex] = {
					...updatedBlocks[swapIndex],
					order: currentBlock.order,
				};

				// Sort blocks by order
				updatedBlocks.sort((a, b) => a.order - b.order);

				// Update section
				updatedSections[currentSectionIndex] = {
					...currentSection,
					blocks: updatedBlocks,
				};
			}
		}

		// Update plan with all changes
		const updatedPlan = { ...plan, sections: updatedSections };
		onPlanUpdate(updatedPlan);
		setIsDirty(true);
	};

	const handleBlockMediaRemove = (blockId, mediaId) => {
		try {
			// Update local state only
			const updatedSections = plan.sections.map((section) => ({
				...section,
				blocks: section.blocks.map((block) => {
					if (block.id === blockId) {
						return {
							...block,
							media: (block.media || []).filter(
								(m) => m.id !== mediaId
							),
						};
					}
					return block;
				}),
			}));

			onPlanUpdate({ ...plan, sections: updatedSections });
			setIsDirty(true); // Mark the plan as dirty
		} catch (error) {
			console.error('Error removing media from block:', error);
			message.error('Failed to remove media from block');
		}
	};

	const handleBlockMediaUpdate = (blockId, mediaIds) => {
		try {
			// Get all media objects from the plan's media array that match the mediaIds
			const selectedMedia = (plan.media || []).filter((m) =>
				mediaIds.has(m.id)
			);

			// Update the block's media in the local state only
			const updatedSections = plan.sections.map((section) => ({
				...section,
				blocks: section.blocks.map((block) => {
					if (block.id === blockId) {
						return {
							...block,
							media: selectedMedia,
						};
					}
					return block;
				}),
			}));

			onPlanUpdate({ ...plan, sections: updatedSections });
			setIsDirty(true); // Mark the plan as dirty

			// Reset selection state and close modal
			setSelectedMediaIds(new Set());
			setIsMediaModalOpen(false);
			setSelectedBlockId(null);
			setSelectedSectionId(null);
		} catch (error) {
			console.error('Error updating block media:', error);
			message.error('Failed to update block media');
		}
	};

	// Memoized Block Component
	const PlanBlock = React.memo(
		({ block, section, onBlockUpdate, isEditMode, onDeleteBlock }) => {
			// Local state refs to avoid re-renders
			const localContentRef = useRef(block.content || '');
			const localTitleRef = useRef(block.title || '');
			const localNotesRef = useRef(block.notes || '');
			const localDurationRef = useRef(block.estimatedDuration || '');

			// Debounced update functions
			const debouncedUpdate = useMemo(
				() =>
					debounce((field, value) => {
						onBlockUpdate(section.id, block.id, field, value);
					}, 1000),
				[section.id, block.id, onBlockUpdate]
			);

			// Update handlers that don't trigger re-renders
			const handleContentChange = (e) => {
				localContentRef.current = e.target.value;
				debouncedUpdate('content', e.target.value);
			};

			const handleTitleChange = (e) => {
				localTitleRef.current = e.target.value;
				debouncedUpdate('title', e.target.value);
			};

			const handleNotesChange = (e) => {
				localNotesRef.current = e.target.value;
				debouncedUpdate('notes', e.target.value);
			};

			const handleDurationChange = (e) => {
				localDurationRef.current = e.target.value;
				debouncedUpdate('estimatedDuration', e.target.value);
			};

			return (
				<div className='block'>
					{isEditMode ? (
						<>
							<input
								type='text'
								defaultValue={localTitleRef.current}
								onChange={handleTitleChange}
								placeholder='Block Title'
							/>
							<textarea
								defaultValue={localContentRef.current}
								onChange={handleContentChange}
								placeholder='Block Content'
							/>
							<textarea
								defaultValue={localNotesRef.current}
								onChange={handleNotesChange}
								placeholder='Notes'
							/>
							<input
								type='text'
								defaultValue={localDurationRef.current}
								onChange={handleDurationChange}
								placeholder='Estimated Duration'
							/>
							<button
								onClick={() =>
									onDeleteBlock(section.id, block.id)
								}>
								Delete Block
							</button>
						</>
					) : (
						<>
							<h3>{localTitleRef.current}</h3>
							<div className='content'>
								{localContentRef.current}
							</div>
							{localNotesRef.current && (
								<div className='notes'>
									{localNotesRef.current}
								</div>
							)}
							{localDurationRef.current && (
								<div className='duration'>
									{localDurationRef.current}
								</div>
							)}
						</>
					)}
				</div>
			);
		}
	);

	// Similar optimization for PlanSection component
	const PlanSection = React.memo(
		({ section, onSectionUpdate, isEditMode }) => {
			const [localTitle, setLocalTitle] = useState(section.title || '');
			const [localDescription, setLocalDescription] = useState(
				section.description || ''
			);
			const [localDuration, setLocalDuration] = useState(
				section.estimatedDuration || ''
			);

			const debouncedTitleUpdate = useMemo(
				() =>
					debounce(
						(value) => onSectionUpdate(section.id, 'title', value),
						500
					),
				[section.id, onSectionUpdate]
			);

			const debouncedDescriptionUpdate = useMemo(
				() =>
					debounce(
						(value) =>
							onSectionUpdate(section.id, 'description', value),
						500
					),
				[section.id, onSectionUpdate]
			);

			const debouncedDurationUpdate = useMemo(
				() =>
					debounce(
						(value) =>
							onSectionUpdate(
								section.id,
								'estimatedDuration',
								value
							),
						500
					),
				[section.id, onSectionUpdate]
			);

			// ... rest of the section component
		}
	);

	const renderBlock = (block, section) => {
		const isCollapsed = collapsedBlocks.has(block.id);
		const mediaArray = block.media
			? Array.isArray(block.media)
				? block.media
				: [block.media]
			: [];

		const renderBlockContent = () => {
			switch (block.type) {
				case 'worship':
					return (
						<>
							<input
								type='text'
								className='block-title-input'
								value={block.songName || ''}
								onChange={(e) =>
									handleBlockChange(
										section.id,
										block.id,
										'songName',
										e.target.value
									)
								}
								placeholder='Song Name'
							/>
							<input
								type='text'
								className='block-title-input'
								value={block.songKey || ''}
								onChange={(e) =>
									handleBlockChange(
										section.id,
										block.id,
										'songKey',
										e.target.value
									)
								}
								placeholder='Key'
							/>
						</>
					);
				default:
					return (
						<input
							type='text'
							className='block-title-input'
							value={block.title || ''}
							onChange={(e) =>
								handleBlockChange(
									section.id,
									block.id,
									'title',
									e.target.value
								)
							}
							placeholder='Block Title'
						/>
					);
			}
		};

		return (
			<div
				key={block.id}
				className={`plan-block ${block.type}-block`}>
				<div className='block-header'>
					<div className='block-header-left'>
						<Button
							type='text'
							className='collapse-btn'
							onClick={() => toggleBlock(block.id)}
							icon={
								<span className='material-icons'>
									{isCollapsed
										? 'chevron_right'
										: 'expand_more'}
								</span>
							}
						/>
						<Select
							className='block-type-select'
							value={block.type}
							onChange={(value) =>
								handleBlockChange(
									section.id,
									block.id,
									'type',
									value
								)
							}>
							{BLOCK_TYPES.map((type) => (
								<Option
									key={type}
									value={type}>
									{type.charAt(0).toUpperCase() +
										type.slice(1)}
								</Option>
							))}
						</Select>
						{renderBlockContent()}
						{isEditMode && (
							<Button
								type='text'
								danger
								className='remove-block-btn'
								onClick={() =>
									handleRemoveBlock(section.id, block.id)
								}
								icon={
									<span className='material-icons'>
										delete
									</span>
								}
							/>
						)}
					</div>
				</div>

				{!isCollapsed && (
					<>
						<div className='block-content'>
							{block.type !== 'worship' && (
								<textarea
									className='block-content-input'
									value={block.content || ''}
									onChange={(e) =>
										handleBlockChange(
											section.id,
											block.id,
											'content',
											e.target.value
										)
									}
									placeholder='Content'
									rows={3}
								/>
							)}
							<textarea
								className='block-notes-input'
								value={block.notes || ''}
								onChange={(e) =>
									handleBlockChange(
										section.id,
										block.id,
										'notes',
										e.target.value
									)
								}
								placeholder='Notes'
								rows={2}
							/>
						</div>

						{/* Media section */}
						<div className='block-media'>
							<div className='block-media-list'>
								{mediaArray.map((media) => (
									<div
										key={media.id}
										className='block-media-item'>
										<span className='material-icons'>
											{media.fileType?.startsWith(
												'image/'
											)
												? 'image'
												: media.fileType?.startsWith(
														'video/'
												  )
												? 'videocam'
												: media.fileType?.startsWith(
														'audio/'
												  )
												? 'audiotrack'
												: 'insert_drive_file'}
										</span>
										<span className='media-name'>
											{media.name}
										</span>
										{isEditMode && (
											<button
												className='remove-media-button'
												onClick={() =>
													handleBlockMediaRemove(
														block.id,
														media.id
													)
												}>
												<span className='material-icons'>
													close
												</span>
											</button>
										)}
									</div>
								))}
							</div>
							{isEditMode && (
								<button
									className='add-media-button'
									onClick={() => {
										setSelectedBlockId(block.id);
										setSelectedSectionId(section.id);
										setIsMediaModalOpen(true);
									}}>
									<span className='material-icons'>
										add_photo_alternate
									</span>
									<span>Add Media</span>
								</button>
							)}
						</div>
					</>
				)}
			</div>
		);
	};

	// Add media selection modal
	const renderMediaModal = () => (
		<Modal
			title='Select Media'
			open={isMediaModalOpen}
			onCancel={() => {
				setIsMediaModalOpen(false);
				setSelectedBlockId(null);
				setSelectedSectionId(null);
				setSelectedMediaIds(new Set());
			}}
			footer={[
				<Button
					key='cancel'
					onClick={() => {
						setIsMediaModalOpen(false);
						setSelectedBlockId(null);
						setSelectedSectionId(null);
						setSelectedMediaIds(new Set());
					}}>
					Cancel
				</Button>,
				<Button
					key='submit'
					type='primary'
					disabled={selectedMediaIds.size === 0}
					onClick={() => {
						handleBlockMediaUpdate(
							selectedBlockId,
							selectedMediaIds
						);
					}}>
					Add Selected Media
				</Button>,
			]}>
			<div className='media-selection-list'>
				{plan.media?.length > 0 ? (
					plan.media.map((media) => (
						<div
							key={media.id}
							className={`media-selection-item ${
								selectedMediaIds.has(media.id) ? 'selected' : ''
							}`}
							onClick={() => {
								const newSelected = new Set(selectedMediaIds);
								if (newSelected.has(media.id)) {
									newSelected.delete(media.id);
								} else {
									newSelected.add(media.id);
								}
								setSelectedMediaIds(newSelected);
							}}>
							<span className='material-icons'>
								{media.fileType?.startsWith('image/')
									? 'image'
									: media.fileType?.startsWith('video/')
									? 'videocam'
									: media.fileType?.startsWith('audio/')
									? 'audiotrack'
									: 'insert_drive_file'}
							</span>
							<span className='media-name'>{media.name}</span>
							{selectedMediaIds.has(media.id) && (
								<span className='material-icons selected-icon'>
									check_circle
								</span>
							)}
						</div>
					))
				) : (
					<div className='no-media-message'>
						No media available. Please add media to the plan first.
					</div>
				)}
			</div>
		</Modal>
	);

	// Update CSS for the media section
	const mediaStyles = {
		'--media-grid-columns': '2', // Default to 2 columns
	};

	return (
		<div
			className='plan-content'
			style={{ ...styleVars, ...mediaStyles }}>
			{plan.sections?.map((section, index) => (
				<div
					key={section.id}
					className={`plan-section`}>
					<div className='section-content'>
						<div className='section-header-row'>
							{isEditMode && (
								<div className='reorder-buttons'>
									<Button
										type='text'
										disabled={section.order <= 1}
										onClick={() =>
											handleMoveSection(section.id, 'up')
										}
										icon={
											<span className='material-icons'>
												arrow_upward
											</span>
										}
									/>
									<Button
										type='text'
										disabled={
											section.order >=
											plan.sections.length
										}
										onClick={() =>
											handleMoveSection(
												section.id,
												'down'
											)
										}
										icon={
											<span className='material-icons'>
												arrow_downward
											</span>
										}
									/>
								</div>
							)}
							<Button
								type='text'
								className='collapse-btn'
								onClick={() => toggleSection(section.id)}
								icon={
									<span className='material-icons'>
										{collapsedSections.has(section.id)
											? 'chevron_right'
											: 'expand_more'}
									</span>
								}
							/>
							<input
								type='number'
								className='duration-input'
								value={section.estimatedDuration || ''}
								onChange={(e) =>
									handleSectionChange(
										section.id,
										'estimatedDuration',
										e.target.value
									)
								}
								placeholder='Duration'
							/>
							<input
								type='text'
								className='title-input'
								value={section.title}
								onChange={(e) =>
									handleSectionChange(
										section.id,
										'title',
										e.target.value
									)
								}
								placeholder='Section Title'
							/>
							{isEditMode && (
								<Button
									type='text'
									danger
									className='remove-section-btn'
									onClick={() =>
										handleRemoveSection(section.id)
									}
									icon={
										<span className='material-icons'>
											delete
										</span>
									}
								/>
							)}
						</div>
						{!collapsedSections.has(section.id) && (
							<textarea
								className='description-input'
								value={section.description || ''}
								onChange={(e) =>
									handleSectionChange(
										section.id,
										'description',
										e.target.value
									)
								}
								placeholder='Section Description'
								rows={2}
							/>
						)}
					</div>

					{!collapsedSections.has(section.id) && (
						<div className='plan-blocks'>
							{section.blocks?.map((block) =>
								renderBlock(block, section)
							)}
							{isEditMode && (
								<Button
									type='dashed'
									className='add-block-btn'
									onClick={() => handleAddBlock(section.id)}
									icon={
										<span className='material-icons'>
											add
										</span>
									}>
									Add Block
								</Button>
							)}
						</div>
					)}
				</div>
			))}
			{isEditMode && (
				<Button
					type='dashed'
					onClick={handleAddSection}
					icon={<span className='material-icons'>add</span>}>
					Add Section
				</Button>
			)}
			{renderMediaModal()}
		</div>
	);
}

export default React.memo(PlanContent);
